import React from 'react';
import { CheckIcon, InfoIcon, LockKeyholeIcon, XIcon } from 'lucide-react';
import { PractitionerFlowShell } from '../components/practitioners/PractitionerFlowShell';
import { FoundingOfferBanner } from '../components/practitioners/FoundingOfferBanner';
import { AccountForm } from '../components/practitioners/AccountForm';
import { MembershipPlans } from '../components/practitioners/MembershipPlans';
import { ResumeSignIn } from '../components/practitioners/ResumeSignIn';
import { usePractitionerSignup } from '../hooks/usePractitionerSignup';
import { foundingPlans } from '../data/foundingOffer';
import type { SignupStep } from '../types/practitionerSignup';
interface PractitionerJoinPageProps {
  initialStep: SignupStep;
}
export function PractitionerJoinPage({
  initialStep
}: PractitionerJoinPageProps) {
  const flow = usePractitionerSignup(initialStep);
  const selectedPlan = foundingPlans.find(p => p.id === flow.paymentModalPlan);
  const selectedPlanName = foundingPlans.find(p => p.id === flow.selectedPlan)?.name ?? null;
  if (flow.step === 'account') {
    const emailExists = flow.accountError?.code === 'email_exists';
    return <PractitionerFlowShell currentStep={0} data-id="element-893" data-mp="2d6hyrwk5hevw">
        <div className="max-w-xl mx-auto px-6" data-id="element-894" data-mp="2875dvhcuahvy">
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.1] tracking-tight text-center" data-id="element-895" data-mp="288gi1ia75rnl">
            Create your practitioner account
          </h1>

          <p className="mt-5 text-lg text-charcoal/70 font-light leading-relaxed text-center" data-id="element-896" data-mp="30v9maamsp92c">
            Join Fem Entity and become part
            of Canada's national women's
            health network. Create your
            account, choose your membership,
            then complete your practitioner
            profile and verification in the
            Fem Entity app.
          </p>

          <div className="mt-10 rounded-3xl border border-purple-200 bg-white px-6 py-8 md:px-9 md:py-9 shadow-[0_18px_50px_-30px_rgba(44,44,44,0.5)]" data-id="element-897" data-mp="1gfg19zq8mo01">
            <AccountForm submitting={flow.accountSubmitting} serverError={flow.accountError?.message ?? null} serverErrorAction={emailExists ? <button type="button" onClick={() => flow.goTo('membership')} className="font-semibold text-red-900 underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-899" data-mp="1cvxhw7zfp1eg">
                    Sign in to choose your
                    membership
                  </button> : null} onSubmit={flow.submitAccount} data-id="element-898" data-mp="1husau2r486ca" />
          </div>

          <p className="mt-6 text-center text-sm text-charcoal/65 leading-relaxed" data-id="element-900" data-mp="1kaosr964wch">
            Already have a Fem Entity
            practitioner account? Sign in
            through the{' '}
            <a href="/get-the-app" className="font-medium text-purple-700 underline underline-offset-2 decoration-purple-300 hover:text-purple-900" data-id="element-901" data-mp="am0cpn3udd5i">
              Fem Entity app
            </a>
            .
          </p>
        </div>
      </PractitionerFlowShell>;
  }
  return <PractitionerFlowShell currentStep={1} data-id="element-902" data-mp="fjikvba1bocg">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-903" data-mp="fir2511jdl3l">
        <div className="max-w-2xl mx-auto text-center" data-id="element-904" data-mp="taoui07132d2">
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.1] tracking-tight" data-id="element-905" data-mp="pan2kir3hnaz">
            Choose your Fem Entity
            membership
          </h1>
        </div>

        <div className="mt-10" data-id="element-906" data-mp="yb33plkd8ico">
          <FoundingOfferBanner data-id="element-907" data-mp="ybupk4o5v1pr" />
        </div>

        <div className="max-w-3xl mx-auto mt-8 space-y-4" data-id="element-908" data-mp="y8yqgd0ku6zq">
          {flow.checkoutCancelled && !flow.needsSignIn && <p role="status" className="flex items-start gap-2.5 rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-charcoal/80" data-id="element-909" data-mp="1mej4bbi8lu7d">
                <InfoIcon aria-hidden="true" className="mt-0.5 w-4 h-4 shrink-0 text-purple-700" data-id="element-910" data-mp="1w5424wq36up8" />
                Checkout was cancelled.
                You haven't been charged.
                Choose a membership whenever
                you're ready.
              </p>}

          {flow.checkoutError && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-id="element-911" data-mp="18hcq7u8gkjoh">
              {flow.checkoutError}
            </p>}

          {flow.needsSignIn && <ResumeSignIn planName={selectedPlanName} submitting={flow.signInSubmitting} error={flow.signInError} onSubmit={flow.resumeWithSignIn} onCancel={flow.cancelSignIn} data-id="element-912" data-mp="2oryl1p799m0l" />}
        </div>

        <div className="mt-12" data-id="element-913" data-mp="3ukjwt4u2ebtk">
          <MembershipPlans selectedPlan={flow.selectedPlan} redirectingPlan={flow.redirectingPlan} onChoose={flow.choosePlan} data-id="element-914" data-mp="3ql2tagy77or1" />
        </div>

        <p className="mt-10 max-w-2xl mx-auto text-center text-sm text-charcoal/60 leading-relaxed" data-id="element-915" data-mp="3bnjvkp3jm17g">
          Payment details are entered
          securely on Stripe. Fem Entity
          does not see or store your card
          information. Memberships renew
          monthly and are governed by the{' '}
          <a href="/terms-of-sale" target="_blank" rel="noopener noreferrer" className="font-medium text-purple-700 underline underline-offset-2 decoration-purple-300 hover:text-purple-900 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-916" data-mp="34cnlh1yoi3v5">
            Practitioner Terms and Conditions of Sale
          </a>
          .
        </p>
      </div>

      {/* ------------------------------------------------
          FEM ENTITY PAYMENT CONFIRMATION MODAL
       ------------------------------------------------- */}

      {selectedPlan && <div className="fixed inset-0 z-[60] overflow-y-auto bg-charcoal/50 px-4 py-6 sm:px-5 sm:py-8" role="dialog" aria-modal="true" aria-labelledby="membership-modal-title" data-id="element-917" data-mp="3e1f8qwssnzzq">
          <div className="relative mx-auto my-auto w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl sm:mt-[8vh]" data-id="element-918" data-mp="22lqoid6xdf2m">
            <button type="button" onClick={flow.cancelPaymentModal} disabled={flow.redirectingPlan !== null} aria-label="Close" className="absolute right-5 top-5 rounded-full p-2 text-charcoal/50 hover:bg-purple-50 hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 disabled:opacity-40" data-id="element-919" data-mp="1z4ugyxa1ou52">
              <XIcon aria-hidden="true" className="h-5 w-5" data-id="element-920" data-mp="1gca1qin9qnjq" />
            </button>

            <div className="px-7 py-8 md:px-9 md:py-10" data-id="element-921" data-mp="1q4y0hixt71l3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100" data-id="element-922" data-mp="1q6isp1wni8fs">
                <LockKeyholeIcon aria-hidden="true" className="h-5 w-5 text-purple-700" data-id="element-923" data-mp="1lbmq57tsg2of" />
              </div>

              <h2 id="membership-modal-title" className="mt-6 font-serif text-3xl text-charcoal" data-id="element-924" data-mp="so907toic7dt">
                Confirm your membership
              </h2>

              <p className="mt-3 text-charcoal/65 leading-relaxed" data-id="element-925" data-mp="c5lgwz8rd0q4">
                You're joining Fem Entity
                as a founding member.
              </p>

              <div className="mt-7 rounded-2xl border border-purple-200 bg-purple-50 px-5 py-5" data-id="element-926" data-mp="l604y6d3wtxb">
                <div className="flex items-center justify-between gap-4" data-id="element-927" data-mp="q539lpp6cy4r">
                  <span className="font-serif text-2xl text-charcoal" data-id="element-928" data-mp="q2qr9bfi8lr6">
                    {selectedPlan.name}
                  </span>

                  <span className="font-serif text-xl text-purple-800" data-id="element-929" data-mp="zu4otdnwbbho">
                    2 months free
                  </span>
                </div>

                <p className="mt-2 text-sm text-charcoal/60" data-id="element-930" data-mp="14vaj3fx7f6tb">
                  <s className="decoration-charcoal/45" data-id="element-931" data-mp="18wdw7unvaoqu">
                    <span className="sr-only" data-id="element-932" data-mp="1dtw89tx6lk32">Regular price </span>
                    {selectedPlan.price}
                    {selectedPlan.priceSuffix}
                  </s>{' '}
                  · Then {selectedPlan.price}
                  {selectedPlan.priceSuffix} from Month 3
                </p>
              </div>

              <div className="mt-6 space-y-3" data-id="element-933" data-mp="2paqk5nn5ro4r">
                <div className="flex items-start gap-3" data-id="element-934" data-mp="2pdmi3bz0act7">
                  <CheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" data-id="element-935" data-mp="2kdguq7zrrcgx" />
                  <div data-id="element-936" data-mp="3d130nmwgyt6y">
                    <p className="font-semibold text-charcoal" data-id="element-937" data-mp="3d0ulfocvjbsl">
                      $0 due today
                    </p>
                    <p className="text-sm text-charcoal/60" data-id="element-938" data-mp="38930btchly9u">
                      Your first two months
                      are complimentary.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-939" data-mp="4mlzgz4uxj9e">
                  <CheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" data-id="element-940" data-mp="3vzoy8rss2264" />
                  <div data-id="element-941" data-mp="z6uvufl0qyvy">
                    <p className="font-semibold text-charcoal" data-id="element-942" data-mp="u6gnewfi9mxe">
                      Payment method required
                    </p>
                    <p className="text-sm text-charcoal/60" data-id="element-943" data-mp="186hmq7g4gmar">
                      Stripe securely saves
                      your payment method for
                      billing after the trial.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-944" data-mp="1d5b2k6ivspe3">
                  <CheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" data-id="element-945" data-mp="1d2owycfjwjyi" />
                  <div data-id="element-946" data-mp="1mvnkdqzba4gj">
                    <p className="font-semibold text-charcoal" data-id="element-947" data-mp="1mv4bezrn2qen">
                      Your founding rate is
                      locked in
                    </p>
                    <p className="text-sm text-charcoal/60" data-id="element-948" data-mp="1vw29l1fec3pb">
                      Your selected rate begins
                      in month three while your
                      membership remains active.
                    </p>
                  </div>
                </div>
              </div>

              {flow.checkoutError && <p role="alert" className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-id="element-949" data-mp="26g4ljek0fi4b">
                  {flow.checkoutError}
                </p>}

              <button type="button" onClick={flow.continueToPayment} disabled={flow.redirectingPlan !== null} className="mt-8 w-full inline-flex items-center justify-center rounded-full bg-purple-700 px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2" data-id="element-950" data-mp="21htc34xmn9mm">
                {flow.redirectingPlan ? 'Opening secure payment…' : 'Continue to secure payment'}
              </button>

              <p className="mt-4 text-center text-xs text-charcoal/60 leading-relaxed" data-id="element-951" data-mp="36p48nfq8kgoy">
                You'll enter your card details
                securely on Stripe. By continuing,
                you agree to the{' '}
                <a href="/terms-of-sale" target="_blank" rel="noopener noreferrer" className="font-medium text-purple-700 underline underline-offset-2 decoration-purple-300 hover:text-purple-900 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-952" data-mp="2bny9qekd557">
                  Practitioner Terms and Conditions of Sale
                </a>
                .
              </p>
            </div>
          </div>
        </div>}
    </PractitionerFlowShell>;
}